/**
 * Créer un handler pour gérer le drop d'un nœud depuis la palette
 */
export const createHandleDrop = (
  reactFlowInstance,
  setNodes,
  callFlowData,
  setCallFlowData,
  setPendingRoutingToOpen,
  handleEditNode,
  handleDeleteNode,
  saveCallFlowToDatabase,
  previousCallFlowDataRef
) => {
  return (event) => {
    event.preventDefault();
    if (!reactFlowInstance) {
      return;
    }
    const nodeData = event.dataTransfer.getData('application/reactflow');
    if (!nodeData) {
      return;
    }
    const parsedNode = JSON.parse(nodeData);
    
    // Gestion spéciale pour survey
    if (parsedNode.id === 'survey') {
      // Créer l'objet survey s'il n'existe pas
      setCallFlowData(prev => {
        const existingSurvey = prev.survey || {};
        
        const newSurvey = {
          enabled: true,
          questions: existingSurvey.questions || {},
          timeout: existingSurvey.timeout || "2",
          goodByeMessage: existingSurvey.goodByeMessage || {
            type: "Text2speech",
            language: prev.language || "en-US",
            text: "Quel est le taux de satisfaction ?",
            audioFileUrl: ""
          }
        };
        
        const updated = {
          ...prev,
          survey: newSurvey
        };
        
        
        // Réinitialiser la référence pour forcer la régénération
        previousCallFlowDataRef.current = null;
        
        // Sauvegarder dans la base de données
        saveCallFlowToDatabase(updated)
          .then(() => {
          })
          .catch((error) => {
            console.error('❌ Error saving survey:', error);
          });
        
        return updated;
      });
      
      return; // Le diagramme se régénérera automatiquement avec le survey
    }
    
    // Gestion spéciale pour workflow
    if (parsedNode.id === 'workflow') {
      const timestamp = Date.now() % 1000;
      
      // Créer un nouveau workflow dans callFlowData
      const newWorkflow = {
        enabled: true, // Activé par défaut
        triggerEvent: '',
        webhook: {
          method: 'POST',
          url: '',
          body: ''
        },
        dataQuery: {
          method: 'GET',
          url: '',
          mapping: {}
        }
      };
      
      // Ajouter le nouveau workflow au tableau workflow
      setCallFlowData(prev => {
        const updatedWorkflow = [...(prev.workflow || [])];
        updatedWorkflow.push(newWorkflow);
        
        const updated = {
          ...prev,
          workflow: updatedWorkflow
        };
        
        
        // Réinitialiser la référence pour forcer la régénération
        previousCallFlowDataRef.current = null;
        
        // Sauvegarder dans la base de données
        saveCallFlowToDatabase(updated)
          .then(() => {
          })
          .catch((error) => {
            console.error('❌ Error saving workflow:', error);
          });
        
        return updated;
      });
      
      return; // Le diagramme se régénérera automatiquement avec le nouveau workflow
    }
    
    // Liste des types de routing à gérer (ceux qui créent un routing dans callFlowData)
    // Ajout de transfer-queue, transfer-user, transfer-phone, disconnect qui sont maintenant des types de routing
    const routingTypes = ['routing', 'ivr', 'prompt', 'check-variable', 'check-days', 'check-time', 'check-regex', 'transfer-queue', 'transfer-user', 'transfer-phone', 'disconnect'];
    
    // Traitement spécial pour les nœuds routing
    if (routingTypes.includes(parsedNode.id)) {
      // Générer un nom unique pour le routing
      const timestamp = Date.now() % 1000;
      
      // Déterminer le type de routing basé sur l'ID du nœud droppé
      let routingType = 'queue';
      let routingName = `Routing_${timestamp}`;
      
      if (parsedNode.id === 'ivr') {
        routingType = 'ivr';
        routingName = `IVR_${timestamp}`;
      } else if (parsedNode.id === 'prompt') {
        routingType = 'prompt';
        routingName = `Prompt_${timestamp}`;
      } else if (parsedNode.id === 'check-variable') {
        routingType = 'check_variable';
        routingName = `CheckVar_${timestamp}`;
      } else if (parsedNode.id === 'check-days') {
        routingType = 'check_days';
        routingName = `CheckDays_${timestamp}`;
      } else if (parsedNode.id === 'check-time') {
        routingType = 'time';
        routingName = `CheckTime_${timestamp}`;
      } else if (parsedNode.id === 'check-regex') {
        routingType = 'regexp';
        routingName = `Regexp_${timestamp}`;
      } else if (parsedNode.id === 'transfer-queue') {
        routingType = 'queue';
        routingName = `Queue_${timestamp}`;
      } else if (parsedNode.id === 'transfer-user') {
        routingType = 'transfer';
        routingName = `TransferUser_${timestamp}`;
      } else if (parsedNode.id === 'transfer-phone') {
        routingType = 'transfer';
        routingName = `TransferPhone_${timestamp}`;
      } else if (parsedNode.id === 'disconnect') {
        routingType = 'disconnect';
        routingName = `Disconnect_${timestamp}`;
      }
      
      // L'ID du routing est le routingName
      const routingKey = routingName;
      const routingId = `routing-${routingKey}`;
      
      
      // Calculer la position dans le diagramme
      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });
      
      // Créer le nouveau routing directement dans callFlowData
      const newRouting = {
        enabled: true,
        displayName: '',
        routingName: routingName,
        type: routingType,
      };
      
      // Configuration spécifique selon le type
      if (routingType === 'queue') {
        newRouting.queueDisplayName = '';
        newRouting.queueId = '';
      } else if (routingType === 'transfer') {
        // Déterminer targetType selon parsedNode.id
        if (parsedNode.id === 'transfer-user') {
          newRouting.targetType = 'user';
          newRouting.targetId = '';
          newRouting.displayName = '';
        } else if (parsedNode.id === 'transfer-phone') {
          newRouting.targetType = 'phone';
          newRouting.targetId = '';
        }
      } else if (routingType === 'disconnect') {
        // Pas de config spéciale pour disconnect
      } else if (routingType === 'ivr') {
        newRouting.expectedDigitNumber = 1;
        newRouting.lastTone = '#';
        newRouting.timeout = 10;
        newRouting.audio = {
          type: 'text2speech',
          language: callFlowData?.language || 'en-US',
          text: '',
          audioFileUrl: '',
        };
        newRouting.fallbackAction = {
          type: 'disconnect',
          targetType: '',
          targetId: '',
          targetRoutingName: '',
        };
        newRouting.options = {};
      } else if (routingType === 'prompt') {
        newRouting.audio = {
          type: 'text2speech',
          language: callFlowData?.language || 'en-US',
          text: '',
          audioFileUrl: '',
        };
      } else if (routingType === 'check_days') {
        newRouting.daysList = [];
      } else if (routingType === 'check_variable' || routingType === 'time' || routingType === 'regexp') {
        newRouting.conditions = {};
      }
      
      // Ajouter le routing au callFlowData
      setCallFlowData((prev) => {
        const updated = {
          ...prev,
          routing: {
            ...(prev.routing || {}),
            [routingKey]: newRouting,
          },
        };
        
        return updated;
      });
      
      // Le node sera créé automatiquement par le useEffect qui génère les nodes
      // Marquer le routing à ouvrir une fois que callFlowData est mis à jour
  
      setPendingRoutingToOpen(routingId);
      
      return;
    }
    
    // Comportement par défaut pour les autres nœuds
    // Calculer la position dans le diagramme
    const position = reactFlowInstance.screenToFlowPosition({
      x: event.clientX,
      y: event.clientY,
    });
    // Générer un ID unique pour le nouveau nœud
    const newNodeId = `${parsedNode.id}-${Date.now()}`;
    // Créer le nouveau nœud
    const newNode = {
      id: newNodeId,
      type: 'default',
      position,
      deletable: true,
      data: {
        label: (
          <div className={`node-${parsedNode.id}`}>
            <div className="node-title">
              {parsedNode.title}
            </div>
            <div className="node-separator"></div>
            <div className="node-info">
              <div className="node-info-label">{parsedNode.description}</div>
            </div>
          </div>
        ),
      },
      style: {
        background: 'rgba(255, 255, 255, 0.95)',
        border: 'none',
        borderRadius: '10px',
        padding: '15px',
        minWidth: '200px',
        boxShadow: '0 4px 12px rgba(91, 95, 199, 0.15)',
      },
    };
    // Ajouter le nœud à la liste
    setNodes((nds) => nds.concat(newNode));
  };
};

/**
 * Créer un handler pour gérer le drag over (permettre le drop)
 */
export const createHandleDragOver = () => {
  return (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  };
};
